import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {HStack, Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    UserBody,
    UserBodySkeleton,
    CreateOrEditUser,
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton, Button, RenewalMailConfirmation
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {Plus, CircleCheckBig, StickyNote, MailWarning} from 'lucide-react';
import {toaster} from "@/components/ui/toaster";

const PER_PAGE = 20;

export const UserList = () => {
    const navigate = useNavigate();
    const {language, availableLanguages, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const [entities, setEntities] = useState([]);

    const triggerFiscalJob = useCallback((userIds = null) => {
        api.ApiService.create(`user/fiscal-attestations`, {userIds})
            .then(() => {
                toaster.create({
                    title: 'Génération lancée',
                    description: userIds
                        ? 'Le job pour cet utilisateur a été ajouté.'
                        : 'Le job global a été ajouté à la file d\'attente.',
                    type: 'success',
                })
            })
            .catch(() => {
                toaster.create({
                    title: 'Erreur',
                    description: 'Impossible de lancer la génération',
                    type: 'error',
                })
            });
    }, [api.ApiService]);

    const possibleSorts = [
        ['createdAt', 'desc', t('sentences.sortByField', {field: t('fields.createdAt'), order: t('orders.desc')})],
        ['createdAt', 'asc', t('sentences.sortByField', {field: t('fields.createdAt'), order: t('orders.asc')})],
        ['firstname', 'desc', t('sentences.sortByField', {field: t('fields.firstname'), order: t('orders.desc')})],
        ['firstname', 'asc', t('sentences.sortByField', {field: t('fields.firstname'), order: t('orders.asc')})],
        ['lastname', 'desc', t('sentences.sortByField', {field: t('fields.lastname'), order: t('orders.desc')})],
        ['lastname', 'asc', t('sentences.sortByField', {field: t('fields.lastname'), order: t('orders.asc')})],
    ]

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);
    const [tags, setTags] = useState(null);
    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {}

        /** Retrieve filters param */
        const filter = pageParams.get('filter');
        try {
            result.filter = JSON.parse(filter);
            if (objectUtil.isObject(result.filter)) {
                result.filter = Object.fromEntries(Object.entries(result.filter).filter(([_, value]) => (value ?? []).length > 0));
            }
        } catch (e) {
            console.error(e);
            result.filter = [];
        }
        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve sort param */
        const sort = (pageParams.get('sort') ?? '').split('-');
        result.sort = possibleSorts.find(([col, type]) => sort[0] === col && sort[1] === type) ?? possibleSorts?.[0];

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        if (isNaN(parsedPage) || parsedPage <= 0) {
            result.page = 0;
        } else {
            result.page = parsedPage - 1;
        }
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [filters, setFilters] = useState({});
    const [getParams, setGetParams] = useState({
        sort: defaultParams.sort,
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
        ...(Object.keys(defaultParams.filter ?? {}).length > 0 ? {filter: defaultParams.filter} : {}),
    });

    const {state, realState, isMounted, setState} = useScreenState();


    const reqParams = useRef(null);
    const getPage = useCallback(() => {
        /** If page is different, it's only a pagination action,
         * so no need to show skeleton for paginator
         */
        const newState = reqParams.current !== null &&
        objectUtil
            .difference(getParams, reqParams.current ?? {})
            .filter((e) => !['page', 'sort'].includes(e))
            .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams
        return api.ApiService.get('user', {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: language,
            ...(state === -1 ? {needFilters: 'bool:true'} : {})
        })
            .then(res => {
                if (res?.data?.type === 'success') {
                    setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                    if (res?.data?.value?.facetDistribution) {
                        const facetData = res?.data?.value?.facetDistribution;
                        const dynamicFilters = {};

                        // activated filter (boolean)
                        if (facetData?.activated) {
                            dynamicFilters['activated'] = {
                                placeholder: t('sentences.actionField', {
                                    action: t('actions.filterBy'),
                                    element: t('fields.state_other')
                                }),
                                items: [
                                    {label: t('states.activated'), value: true},
                                    {label: t('states.notActivated'), value: false}
                                ]
                            };
                        }

                        // club filter (non-association, raw string values)
                        if (facetData?.club) {
                            dynamicFilters['club'] = {
                                placeholder: t('sentences.actionField', {
                                    action: t('actions.filterBy'),
                                    element: t('fields.club')
                                }),
                                items: Object.keys(facetData.club).map((clubName) => ({
                                    label: clubName,
                                    value: clubName
                                }))
                            };
                        }

                        setFilters(dynamicFilters);
                    }
                    setEntities(() => (res?.data?.value?.hits ?? []).map((entity) => {
                        return {
                            ...entity,
                        }
                    }));
                }
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2))
    }, [getParams, state]);

    const acceptUser = useCallback((user) => {
        return api.ApiService.update('user', user.id, {activated: true})
            .then(res => {
                if (res?.data?.type === 'success') {
                    setEntities((prevState) => prevState.map((u) => u.id === user.id ? {...u, activated: true} : u));
                }
            })
            .catch(e => console.log(e?.response?.data))
    }, [entities, setEntities]);

    useEffect(() => {
        getPage().then(() => {
            /** Manage persistent state of search query */
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                if ('filter' in getParams) {
                    prev.set('filter', JSON.stringify(getParams.filter));
                } else {
                    prev.delete('filter');
                }

                if ('sort' in getParams) {
                    prev.set('sort', getParams.sort.slice(0, 2).join('-'));
                } else {
                    prev.delete('sort');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            })
        })
    }, [getParams]);


    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(objectUtil.filterByKeys(getParams, ['sort'], false)).length > 0)
    }, [isMounted, entities, getParams]);

    const noDataText = t('sentences.noEntityYet.user');

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<HStack spacing={2}>
                        {/* Relance de renouvellement d'adhésion (envoi en masse) */}
                        <IconButton
                            bg={"secondary.500"}
                            title={t('renewal.title')}
                            onClick={() => {
                                open(<RenewalMailConfirmation
                                    close={close}
                                    successCallback={(count) => {
                                        toaster.create({
                                            title: t('renewal.sentTitle'),
                                            description: t('renewal.sentDescription', {count}),
                                            type: 'success',
                                        });
                                    }}
                                    errorCallback={() => {
                                        toaster.create({
                                            title: 'Erreur',
                                            description: t('renewal.sendError'),
                                            type: 'error',
                                        });
                                    }}
                                />, 'sm');
                            }}
                        >
                            <Icon as={MailWarning} />
                        </IconButton>
                        <IconButton
                            bg={"secondary.500"}
                            onClick={() => triggerFiscalJob()}
                        >
                            <Icon as={StickyNote} />
                        </IconButton>
                        <IconButton
                        bg={"secondary.500"}
                        onClick={(_) => {
                            open(<EntityModalContent
                                title={t('add')}
                                Body={<CreateOrEditUser
                                    successCallback={() => {
                                        navigate(0);
                                    }}
                                />}
                                close={close}
                            />, 'cover')
                        }}
                    >
                        <Icon as={Plus}/>
                    </IconButton></HStack>}
                    setGetParams={setGetParams}
                    filters={filters}
                    defaultParams={defaultParams}
                    sizes={sizes}
                    possibleSorts={possibleSorts}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton
                                Body={UserBodySkeleton}
                                sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity="user"
                                Body={UserBody}
                                EditBody={CreateOrEditUser}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={(e) => `"${[e?.firstname, e?.lastname].filter(Boolean).join(' ')}"`.slice(0, 200)}
                                customButton={(e) => {
                                    return e?.item?.activated === false ?
                                        <Button
                                            visual="icon"
                                            onClick={(_) => acceptUser(e?.item)}
                                        >
                                            <Icon as={CircleCheckBig} boxSize={5}/>
                                        </Button>
                                        : null
                                }
                                }
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton
                    bg={'secondary.500'}
                    onClick={(_) => {
                        open(<EntityModalContent
                            title={t('actions.add')}
                            Body={<CreateOrEditUser
                                successCallback={() => {
                                    navigate(0);
                                }}
                            />}
                            close={close}
                        />, 'cover')
                    }}
                >
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>
    }, [hasData, realState, entities, setEntities, setGetParams, filters, defaultParams, sizes, possibleSorts, itemNumber, close, navigate])

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton
                        Body={UserBodySkeleton}
                        sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>
    }, [isMounted, Table])

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader title={t('sentences.managementOfEntities', {entity: t('objects.user_other')})}
                        description={t('sentences.manageEntities', {
                            action1: t('actions.add'),
                            action2: t('actions.edit'),
                            action3: t('actions.view'),
                            entity: t('objects.user_other')
                        })}/>
            {
                /** Wait for options to be retrieved otherwise, default state isn't took in count */
                Content
            }
        </VStack>
    );
};

export default UserList;
